
import React, { useState } from 'react';
import { TeamStats } from '@/components/team-details/types';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import StatRow from './stats/StatRow';
import StatsHeader from './stats/StatsHeader';
import { calculateHomeStats, calculateAwayStats, calculatePoints } from './stats/StatsCalculator';

interface StatsTableProps {
  homeTeamStats: TeamStats & { team: string };
  awayTeamStats: TeamStats & { team: string };
}

const StatsTable: React.FC<StatsTableProps> = ({ homeTeamStats, awayTeamStats }) => {
  const [showSeasonStats, setShowSeasonStats] = useState(false);

  // Calculate home/away stats
  const homeStats = calculateHomeStats(homeTeamStats);
  const awayStats = calculateAwayStats(awayTeamStats);

  // Calculate season stats (using the full TeamStats objects)
  const homeSeasonStats = {
    played: homeTeamStats.played || 0,
    wins: homeTeamStats.wins || 0,
    draws: homeTeamStats.draws || 0,
    losses: homeTeamStats.losses || 0,
    halfTimeWins: (homeTeamStats.halfTimeStats?.homeWins || 0) + (homeTeamStats.halfTimeStats?.awayWins || 0),
    halfTimeDraws: (homeTeamStats.halfTimeStats?.homeDraws || 0) + (homeTeamStats.halfTimeStats?.awayDraws || 0),
    halfTimeLosses: (homeTeamStats.halfTimeStats?.homeLosses || 0) + (homeTeamStats.halfTimeStats?.awayLosses || 0),
    cleanSheets: (homeTeamStats.fullTimeStats?.homeCleanSheets || 0) + (homeTeamStats.fullTimeStats?.awayCleanSheets || 0),
    btts: (homeTeamStats.fullTimeStats?.homeBTTS || 0) + (homeTeamStats.fullTimeStats?.awayBTTS || 0),
    htBtts: (homeTeamStats.halfTimeStats?.homeBTTS || 0) + (homeTeamStats.halfTimeStats?.awayBTTS || 0),
  };

  const awaySeasonStats = {
    played: awayTeamStats.played || 0,
    wins: awayTeamStats.wins || 0,
    draws: awayTeamStats.draws || 0,
    losses: awayTeamStats.losses || 0,
    halfTimeWins: (awayTeamStats.halfTimeStats?.homeWins || 0) + (awayTeamStats.halfTimeStats?.awayWins || 0),
    halfTimeDraws: (awayTeamStats.halfTimeStats?.homeDraws || 0) + (awayTeamStats.halfTimeStats?.awayDraws || 0),
    halfTimeLosses: (awayTeamStats.halfTimeStats?.homeLosses || 0) + (awayTeamStats.halfTimeStats?.awayLosses || 0),
    cleanSheets: (awayTeamStats.fullTimeStats?.homeCleanSheets || 0) + (awayTeamStats.fullTimeStats?.awayCleanSheets || 0),
    btts: (awayTeamStats.fullTimeStats?.homeBTTS || 0) + (awayTeamStats.fullTimeStats?.awayBTTS || 0),
    htBtts: (awayTeamStats.halfTimeStats?.homeBTTS || 0) + (awayTeamStats.halfTimeStats?.awayBTTS || 0),
  };

  // Use either season stats or home/away stats based on state
  const currentHomeStats = showSeasonStats ? homeSeasonStats : homeStats;
  const currentAwayStats = showSeasonStats ? awaySeasonStats : awayStats;

  // Calculate points based on current stats
  const homePoints = calculatePoints(currentHomeStats);
  const awayPoints = calculatePoints(currentAwayStats);

  // Ensure we have valid numbers for the played matches
  const homePlayed = Math.max(currentHomeStats.played, 1); // Prevent division by zero
  const awayPlayed = Math.max(currentAwayStats.played, 1); // Prevent division by zero

  return (
    <Card className="col-span-3 p-6">
      <div className="grid grid-cols-3 mb-4">
        <h3 className="text-lg font-bold text-right text-blue-600 flex flex-col items-end">
          <span>{homeTeamStats.team}</span>
          <span>{showSeasonStats ? 'Season' : 'Home'} Stats</span>
        </h3>
        <div className="flex justify-center">
          <Button
            onClick={() => setShowSeasonStats(!showSeasonStats)}
            variant="outline"
            className="px-4 py-2"
          >
            {showSeasonStats ? 'Home/Away Stats' : 'Season Stats'}
          </Button>
        </div>
        <h3 className="text-lg font-bold text-left text-red-600 flex flex-col items-start">
          <span>{awayTeamStats.team}</span>
          <span>{showSeasonStats ? 'Season' : 'Away'} Stats</span>
        </h3>
      </div>
      <div className="max-w-4xl mx-auto">
        <table className="w-full">
          <tbody>
            <StatsHeader
              homeMatches={currentHomeStats.played}
              awayMatches={currentAwayStats.played}
              homePoints={homePoints}
              awayPoints={awayPoints}
            />
            <StatRow
              label="Wins"
              homeValue={{ value: currentHomeStats.wins, total: homePlayed }}
              awayValue={{ value: currentAwayStats.wins, total: awayPlayed }}
            />
            <StatRow
              label="Draws"
              homeValue={{ value: currentHomeStats.draws, total: homePlayed }}
              awayValue={{ value: currentAwayStats.draws, total: awayPlayed }}
            />
            <StatRow
              label="Losses"
              homeValue={{ value: currentHomeStats.losses, total: homePlayed }}
              awayValue={{ value: currentAwayStats.losses, total: awayPlayed }}
            />
            <StatRow
              label="Half Time Wins"
              homeValue={{ value: currentHomeStats.halfTimeWins, total: homePlayed }}
              awayValue={{ value: currentAwayStats.halfTimeWins, total: awayPlayed }}
            />
            <StatRow
              label="Half Time Draws"
              homeValue={{ value: currentHomeStats.halfTimeDraws, total: homePlayed }}
              awayValue={{ value: currentAwayStats.halfTimeDraws, total: awayPlayed }}
            />
            <StatRow
              label="Half Time Losses"
              homeValue={{ value: currentHomeStats.halfTimeLosses, total: homePlayed }}
              awayValue={{ value: currentAwayStats.halfTimeLosses, total: awayPlayed }}
            />
            <StatRow
              label="Clean Sheets"
              homeValue={{ value: currentHomeStats.cleanSheets, total: homePlayed }}
              awayValue={{ value: currentAwayStats.cleanSheets, total: awayPlayed }}
            />
            <StatRow
              label="BTTS"
              homeValue={{ value: currentHomeStats.btts, total: homePlayed }}
              awayValue={{ value: currentAwayStats.btts, total: awayPlayed }}
            />
            <StatRow
              label="HT BTTS"
              homeValue={{ value: currentHomeStats.htBtts, total: homePlayed }}
              awayValue={{ value: currentAwayStats.htBtts, total: awayPlayed }}
            />
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default StatsTable;
